import React, { useEffect } from "react";
import "./App.css";
import "./i18n";
import LoginPageContainer from "./pages/logins/LoginPageContainer";
import { BrowserRouter, Route, Router, Routes } from "react-router-dom";
import EmailValidation from "./pages/emailValidation/EmailValidation";
import LandingPage from "./pages/landing/LandingPage";
import ProtectedRoute from "./requestHelper/ProtectedRoute";
import ErrorBoundary from "./ErrorBoundary";
import "./styles/landingPage.css";
import "./styles/loginPage.css";
import "./styles/newOrderPage.css";
import "./styles/newProduct.css";
import "./styles/card.css";
import "./styles/common-components.css";
import "./styles/typography.css";
import "./styles/icons.css";
import "./styles/modal.css";
import "./styles/button.css";
import "./styles/sampleInformation.css";
import "./styles/viewItems.css";
import "./styles/viewOrderPage.css";
import "./styles/viewOrganizationPage.css";
import "./styles/emailValidation.css";
import "./styles/profilePage.css";
import "./styles/helpAndContactUsPage.css";

import { createTheme, ThemeProvider } from "@mui/material";
import {
	BackgroundColor,
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import { SnackbarProvider } from "./common-components/toast/showToastMessage";
import ResetPassword from "./pages/emailValidation/ResetPassword";

const theme = createTheme({
	palette: {
		mode: "light",
		primary: {
			main: "#2E64C9",
			light: "#2945A9",
			dark: "#E4F2FF",
		},
		secondary: {
			main: "#1A4355",
			dark: "#062E3E",
			light: "#DBF1FF",
		},
		background: {
			default: "#ffffff",
			paper: "#ffffff",
		},
		text: {
			primary: "#121212", // Change this to your desired text color
		},
	},
	typography: {
		fontFamily: [
			'"Arial"',
			'"Segoe UI"',
			'"Roboto"',
			'"Oxygen"',
			'"Ubuntu"',
			'"Cantarell"',
			'"Fira Sans"',
			'"Droid Sans"',
			'"Helvetica Neue"',
			"sans-serif",
		].join(", "),
		fontSize: 16,
		h1: {
			fontSize: 45,
			fontFamily: [
				'"Montserrat"',
				'"Segoe UI"',
				'"Roboto"',
				'"Oxygen"',
				'"Ubuntu"',
				'"Cantarell"',
				'"Fira Sans"',
				'"Droid Sans"',
				'"Helvetica Neue"',
				"sans-serif",
			].join(", "),
		},
		h2: {
			fontSize: 32,
			fontFamily: [
				'"Montserrat"',
				'"Segoe UI"',
				'"Roboto"',
				'"Oxygen"',
				'"Ubuntu"',
				'"Cantarell"',
				'"Fira Sans"',
				'"Droid Sans"',
				'"Helvetica Neue"',
				"sans-serif",
			].join(", "),
		},
		h3: {
			fontSize: 28,
			fontFamily: [
				'"Montserrat"',
				'"Segoe UI"',
				'"Roboto"',
				'"Oxygen"',
				'"Ubuntu"',
				'"Cantarell"',
				'"Fira Sans"',
				'"Droid Sans"',
				'"Helvetica Neue"',
				"sans-serif",
			].join(", "),
		},
		h4: {
			fontSize: 24,
			fontFamily: [
				'"Montserrat"',
				'"Segoe UI"',
				'"Roboto"',
				'"Oxygen"',
				'"Ubuntu"',
				'"Cantarell"',
				'"Fira Sans"',
				'"Droid Sans"',
				'"Helvetica Neue"',
				"sans-serif",
			].join(", "),
		},
		h5: {
			fontSize: 20,
			fontFamily: [
				'"Montserrat"',
				'"Segoe UI"',
				'"Roboto"',
				'"Oxygen"',
				'"Ubuntu"',
				'"Cantarell"',
				'"Fira Sans"',
				'"Droid Sans"',
				'"Helvetica Neue"',
				"sans-serif",
			].join(", "),
		},
		body1: {
			fontSize: 16,
		},
		body2: {
			fontSize: 14,
		},
		caption: {
			fontSize: 12,
		},
		button: {
			fontSize: 16,
		},
	},
	shape: {
		borderRadius: 8,
	},
	components: {
		MuiMenuItem: {
			styleOverrides: {
				root: {
					"&:hover": {
						backgroundColor: BackgroundColor.LIGHT_BLUE,
					},
					"&.Mui-selected": {
						backgroundColor: PrimaryColor.PRIMARY_50,
					},
				},
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				option: {
					"&:hover": {
						backgroundColor: BackgroundColor.LIGHT_BLUE,
					},
					"&.Mui-selected": {
						backgroundColor: PrimaryColor.PRIMARY_50,
					},
				},
				paper: {
					margin: "2px 0px",
					border: "1px solid" + PrimaryColor.PRIMARY_800,
				},
				root: {
					"& .MuiOutlinedInput-root": {
						padding: "4px 9px",
						height: "44px",
						"& fieldset": {
							borderColor: NeutralColor.NEUTRAL_GREY_200,
						},
						"&:not(.Mui-disabled):hover fieldset": {
							borderColor: PrimaryColor.PRIMARY_800,
							borderWidth: "1px",
						},
					},
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					padding: "10px 32px",
					borderRadius: "8px",
				},
			},
		},
		MuiCard: {
			defaultProps: {
				elevation: 0, // Remove shadow by setting elevation to 0
			},
			styleOverrides: {
				root: {
					boxShadow: "none", // Alternatively, ensure boxShadow is removed globally
					borderRadius: "8px",
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: "8px", // Change the border-radius here
						"&:not(.Mui-disabled):not(.Mui-focused):hover fieldset": {
							borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
							borderWidth: "1px",
						},
						"&.Mui-focused fieldset": {
							boxShadow: "0px 0px 6px #2945A940",
							borderWidth: "1.5px",
						},
						"&.Mui-disabled fieldset": {
							borderColor: NeutralColor.NEUTRAL_GREY_50,
						},
						// height: "40px",
					},
					"& .MuiInputBase-input": {
						padding: "8px 14px", // Change the padding as needed
					},
					borderRadius: "8px", // Change the border-radius here
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: "8px", // Change the border-radius here
						"&:not(.Mui-disabled):not(.Mui-focused):hover fieldset": {
							borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
							borderWidth: "1px",
						},
						"&.Mui-focused fieldset": {
							borderWidth: "1.5px",
						},
					},
					"&:not(.Mui-disabled):not(.Mui-focused):hover": {
						borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
						borderWidth: "1px",
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundColor: BackgroundColor.WHITE, // Custom background color for all Paper components
					borderRadius: "8px",
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				input: {
					"&.MuiOutlinedInput-input.Mui-disabled": {
						cursor: "not-allowed",
						WebkitTextFillColor: NeutralColor.NEUTRAL_GREY_50,
					},
					"&.Mui-readOnly, &.MuiInputBase-readOnly": {
						cursor: "not-allowed",
					},
				},
				root: {
					// backgroundColor: BackgroundColor.WHITE,
					borderRadius: "8px",
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: NeutralColor.NEUTRAL_GREY_100,
				},
			},
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					"&:last-child": {
						paddingBottom: "16px",
					},
				},
			},
		},
	},
});

function App() {
	useEffect(() => {
		// Dynamically load the reCAPTCHA script
		const script = document.createElement("script");
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_API_RECAPTCHA_KEY}`;
		script.async = true;
		script.onload = () => {
			console.log("reCAPTCHA script loaded");
			// You can initialize or execute any reCAPTCHA-related logic here.
		};
		document.head.appendChild(script);

		// Clean up script on component unmount
		return () => {
			document.head.removeChild(script);
		};
	}, []);
	return (
		<ThemeProvider theme={theme}>
			<SnackbarProvider>
				<BrowserRouter>
					<Routes>
						<Route
							path="/validate-email"
							element={<EmailValidation></EmailValidation>}
						/>
						<Route
							path="/reset-password"
							element={<ResetPassword></ResetPassword>}
						/>
						<Route path="/login" element={<LoginPageContainer />} />

						<Route
							path="/portal/:page"
							element={
								<ErrorBoundary>
									<ProtectedRoute>
										<LandingPage />
									</ProtectedRoute>
								</ErrorBoundary>
							}
						/>

						{/* Catch-all route for 404 */}
						<Route path="*" element={<LoginPageContainer />} />
					</Routes>
				</BrowserRouter>
			</SnackbarProvider>
		</ThemeProvider>
	);
}

export default App;
